/**
 * Merina HMS - Accessibility Engine
 * ==================================
 * Core logic for managing accessibility features.
 * Uses body-level class injection - ZERO changes to existing components.
 */

const STORAGE_KEY = 'merina_accessibility_settings';

// Default settings
const DEFAULT_SETTINGS = {
  textSize: 'md',        // sm, md, lg, xl
  highContrast: false,
  darkMode: false,
  reduceMotion: false,
  focusMode: false,
  readable: false,
  screenReaderMode: false,
  dyslexiaFont: false,
  cursorSize: 'normal',  // normal, large, xl
};

class AccessibilityEngine {
  constructor() {
    this.settings = this.loadSettings();
    this.listeners = new Set();
    this.initialized = false;
  }

  /**
   * Initialize the engine and apply saved settings
   */
  init() {
    if (this.initialized) return;
    
    this.applyAllSettings();
    this.setupKeyboardShortcuts();
    this.initialized = true;
    
    console.log('[Accessibility] Engine initialized with settings:', this.settings);
  }

  /**
   * Load settings from localStorage
   */
  loadSettings() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.warn('[Accessibility] Failed to load settings:', e);
    }
    return { ...DEFAULT_SETTINGS };
  }

  /**
   * Save settings to localStorage
   */
  saveSettings() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch (e) {
      console.warn('[Accessibility] Failed to save settings:', e);
    }
  }

  /**
   * Subscribe to settings changes
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of changes
   */
  notifyListeners() {
    this.listeners.forEach(cb => cb(this.settings));
  }

  /**
   * Update a single setting
   */
  setSetting(key, value) {
    this.settings[key] = value;
    this.applySetting(key, value);
    this.saveSettings();
    this.notifyListeners();
  }

  /**
   * Get current settings
   */
  getSettings() {
    return { ...this.settings };
  }

  /**
   * Reset all settings to defaults
   */
  resetSettings() {
    this.settings = { ...DEFAULT_SETTINGS };
    this.applyAllSettings();
    this.saveSettings();
    this.notifyListeners();
  }

  /**
   * Apply all settings to the body
   */
  applyAllSettings() {
    Object.entries(this.settings).forEach(([key, value]) => {
      this.applySetting(key, value);
    });
  }

  /**
   * Apply a single setting by adding/removing body classes
   */
  applySetting(key, value) {
    const body = document.body;

    switch (key) {
      case 'textSize':
        // Remove all text size classes from both html and body
        const html = document.documentElement;
        html.classList.remove('a11y-text-sm', 'a11y-text-md', 'a11y-text-lg', 'a11y-text-xl');
        body.classList.remove('a11y-text-sm', 'a11y-text-md', 'a11y-text-lg', 'a11y-text-xl');
        if (value && value !== 'md') {
          html.classList.add(`a11y-text-${value}`);
          body.classList.add(`a11y-text-${value}`);
        }
        break;

      case 'highContrast':
        body.classList.toggle('a11y-high-contrast', value);
        break;

      case 'darkMode':
        body.classList.toggle('a11y-dark-mode', value);
        break;

      case 'reduceMotion':
        body.classList.toggle('a11y-reduce-motion', value);
        break;

      case 'focusMode':
        body.classList.toggle('a11y-focus-mode', value);
        break;

      case 'readable':
        body.classList.toggle('a11y-readable', value);
        break;

      case 'screenReaderMode':
        body.classList.toggle('a11y-screen-reader', value);
        break;

      case 'dyslexiaFont':
        body.classList.toggle('a11y-dyslexia-font', value);
        break;

      case 'cursorSize':
        body.classList.remove('a11y-cursor-large', 'a11y-cursor-xl');
        if (value && value !== 'normal') {
          body.classList.add(`a11y-cursor-${value}`);
        }
        break;

      default:
        break;
    }
  }

  /**
   * Setup keyboard shortcuts for accessibility
   */
  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Alt + A: Toggle accessibility panel (handled by UI)
      // Alt + H: Toggle high contrast
      if (e.altKey && e.key === 'h') {
        e.preventDefault();
        this.setSetting('highContrast', !this.settings.highContrast);
      }
      // Alt + M: Toggle reduce motion
      if (e.altKey && e.key === 'm') {
        e.preventDefault();
        this.setSetting('reduceMotion', !this.settings.reduceMotion);
      }
      // Alt + Plus: Increase text size
      if (e.altKey && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        this.increaseTextSize();
      }
      // Alt + Minus: Decrease text size
      if (e.altKey && e.key === '-') {
        e.preventDefault();
        this.decreaseTextSize();
      }
    });
  }

  /**
   * Increase text size
   */
  increaseTextSize() {
    const sizes = ['sm', 'md', 'lg', 'xl'];
    const currentIndex = sizes.indexOf(this.settings.textSize);
    if (currentIndex < sizes.length - 1) {
      this.setSetting('textSize', sizes[currentIndex + 1]);
    }
  }

  /**
   * Decrease text size
   */
  decreaseTextSize() {
    const sizes = ['sm', 'md', 'lg', 'xl'];
    const currentIndex = sizes.indexOf(this.settings.textSize);
    if (currentIndex > 0) {
      this.setSetting('textSize', sizes[currentIndex - 1]);
    }
  }

  /**
   * Get accessibility stats for screen readers
   */
  getAccessibilityStats() {
    const enabledFeatures = Object.entries(this.settings)
      .filter(([key, value]) => {
        if (key === 'textSize') return value !== 'md';
        if (key === 'cursorSize') return value !== 'normal';
        return value === true;
      })
      .map(([key]) => key);

    return {
      enabledCount: enabledFeatures.length,
      enabledFeatures,
      allSettings: this.settings
    };
  }
}

// Export singleton instance
export const accessibilityEngine = new AccessibilityEngine();
export default accessibilityEngine;
